import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardCheck, Search, Download, Printer, Upload, Loader2, MapPin, ChevronDown,
  CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Trash2, FileText, History, QrCode, X, Camera, ScanLine,
} from "lucide-react";
import { printBlankTemplate } from "@/lib/templates";
import { scanQrFromFile, parseDocNoQr } from "@/lib/qr";
import { Html5Qrcode } from "html5-qrcode";

const num = (n) => (Number(n) || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });

export default function Count() {
  const { isPrivileged, user, can, feature } = useAuth();
  const { activeLocId } = useActiveLocation();
  const canAdjust = can("stock.adjust");
  const canCount = can("stock.count");
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [onlyDiff, setOnlyDiff] = useState(true);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [note, setNote] = useState("");
  const [docNo, setDocNo] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [manual, setManual] = useState({});
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const [schedule, setSchedule] = useState(null);
  const [freqEdits, setFreqEdits] = useState({});
  const scanLast = useRef({ code: "", t: 0 });
  const scannerRef = useRef(null);
  const fileRef = useRef(null);
  const sheetRef = useRef(null);

  const loadLocs = useCallback(() => {
    if (isPrivileged) {
      api.get("/locations").then((r) => {
        setLocations(r.data);
        setLocationId((c) => c || activeLocId || r.data.find((l) => (l.types || [l.type]).includes("store"))?.id || r.data[0]?.id || "");
      }).catch(() => {});
    }
  }, [isPrivileged, activeLocId]);
  useEffect(() => { loadLocs(); }, [loadLocs]);

  const loadSessions = useCallback(() => {
    api.get("/inventory/count/sessions?limit=10").then((r) => setSessions(r.data || [])).catch(() => {});
  }, []);
  const loadSchedule = useCallback(() => {
    api.get("/inventory/count/status").then((r) => setSchedule(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);
  const saveSchedule = async () => {
    const per = {};
    (schedule?.items || []).forEach((it) => {
      const v = freqEdits[it.location_id];
      const days = (v != null && v !== "") ? Number(v) : it.frequency_days;
      if (days > 0) per[it.location_id] = days;
    });
    try {
      await api.put("/inventory/count/schedule", { default_days: schedule?.default_days || 30, per_location: per });
      toast.success("Sayım periyotları kaydedildi");
      loadSchedule();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };
  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      const p = new URLSearchParams();
      if (locationId) p.set("location_id", locationId);
      p.set("q", q);
      api.get(`/inventory?${p}`).then((r) => setResults((r.data.items || []).slice(0, 8))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, locationId]);

  const reset = () => { setRows([]); setSummary(null); setUnmatched([]); };

  const addManual = (it) => {
    setManual((m) => ({ ...m, [it.product_id]: { name: it.name, sku: it.sku, counted: m[it.product_id]?.counted ?? "" } }));
    setQ(""); setResults([]);
  };
  const setManualQty = (pid, v) => setManual((m) => ({ ...m, [pid]: { ...m[pid], counted: v } }));
  const removeManual = (pid) => setManual((m) => { const n = { ...m }; delete n[pid]; return n; });

  const previewManual = async () => {
    const lines = Object.entries(manual)
      .filter(([, v]) => String(v.counted).trim() !== "")
      .map(([pid, v]) => ({ product_id: pid, counted: Number(String(v.counted).replace(",", ".")) }));
    if (!lines.length) return toast.error("En az bir ürün için sayılan miktar girin");
    setLoading(true);
    try {
      const { data } = await api.post("/inventory/count/preview", { location_id: locationId, lines });
      setRows(data.rows || []); setSummary(data.summary); setUnmatched([]);
      toast.success(`${data.rows.length} kalem karşılaştırıldı`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Önizleme başarısız"); }
    finally { setLoading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const p = new URLSearchParams();
      if (locationId) p.set("location_id", locationId);
      const res = await api.get(`/inventory/count/template?${p}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "sayim-foyu.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Excel sayım şablonu indirildi");
    } catch { toast.error("Şablon indirilemedi"); }
  };

  const importSheet = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    const isExcel = ["xlsx", "xlsm"].includes(ext);
    setLoading(true);
    try {
      // Yüklenen belge her durumda inceleme için sayıma eklenir
      try {
        const dfd = new FormData(); dfd.append("file", f);
        const { data: up } = await api.post("/uploads/document", dfd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocUrl(up.url);
        const p = parseDocNoQr(await scanQrFromFile(f).catch(() => null));
        if (p?.doc_no) setDocNo(p.doc_no);
      } catch { /* belge ekleme opsiyonel */ }
      if (isExcel) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("location_id", locationId || "");
        const { data } = await api.post("/inventory/count/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setRows(data.rows || []); setSummary(data.summary); setUnmatched(data.unmatched || []);
        toast.success(`${data.rows.length} kalem içe aktarıldı · belge incelemeye eklendi${data.unmatched?.length ? ` · ${data.unmatched.length} eşleşmeyen` : ""}`);
      } else {
        toast.success("Belge yüklendi (inceleme için ekli). Sayılan miktarları 2b'den girip 'Farkları Hesapla' deyin — sonuç amir onayına gider.");
      }
    } catch (er) { toast.error(apiError(er.response?.data?.detail) || "Yüklenemedi"); }
    finally { setLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const uploadSheet = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocUrl(data.url);
      const p = parseDocNoQr(await scanQrFromFile(f));
      if (p?.doc_no) { setDocNo(p.doc_no); toast.success(`İmzalı föy yüklendi · QR: ${p.doc_no}`); }
      else toast.success("İmzalı föy yüklendi");
    } catch { toast.error("Föy yüklenemedi"); }
    finally { if (sheetRef.current) sheetRef.current.value = ""; }
  };

  const onBarcode = useCallback(async (code) => {
    const now = Date.now();
    if (scanLast.current.code === code && now - scanLast.current.t < 1500) return;
    scanLast.current = { code, t: now };
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}`);
      if (!data?.id) { toast.error(`Ürün bulunamadı: ${code}`); return; }
      setManual((m) => {
        const prev = m[data.id];
        const cur = prev ? (Number(String(prev.counted).replace(",", ".")) || 0) : 0;
        return { ...m, [data.id]: { name: data.name, sku: data.sku, counted: cur + 1 } };
      });
      setScanCount((c) => c + 1);
      toast.success(`+1 · ${data.name}`);
    } catch { toast.error(`Ürün bulunamadı: ${code}`); }
  }, []);

  useEffect(() => {
    if (!scanOpen) return undefined;
    let stopped = false;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode("count-scanner-region", { verbose: false });
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 160 } },
          (txt) => onBarcode(String(txt).trim()), () => {});
      } catch {
        if (!stopped) toast.error("Kamera açılamadı — tarayıcı iznini kontrol edin");
      }
    };
    start();
    return () => {
      stopped = true;
      const s = scannerRef.current;
      if (s) { s.stop().then(() => s.clear()).catch(() => {}); scannerRef.current = null; }
    };
  }, [scanOpen, onBarcode]);

  const apply = async () => {
    const lines = rows.filter((r) => Math.abs(r.diff) > 1e-9).map((r) => ({ product_id: r.product_id, counted: r.counted }));
    if (!lines.length) return toast.error("Uygulanacak fark yok");
    setApplying(true);
    try {
      const { data } = await api.post("/inventory/count/apply", {
        location_id: locationId, lines, note, doc_no: docNo || undefined, document_url: docUrl || undefined,
      });
      if (data?.pending_approval) toast.success("Sayım farkları müdür onayına gönderildi (onaylanınca stok düzeltilir).");
      else toast.success(`Sayım uygulandı · ${data.applied} kalem düzeltildi${data.failed ? ` · ${data.failed} başarısız` : ""}`);
      reset(); setManual({}); setNote(""); setDocNo(""); setDocUrl(""); setConfirm(false);
      loadSessions();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Uygulanamadı"); }
    finally { setApplying(false); }
  };

  const shown = onlyDiff ? rows.filter((r) => Math.abs(r.diff) > 1e-9) : rows;
  const diffLines = rows.filter((r) => Math.abs(r.diff) > 1e-9).length;
  const locName = locations.find((l) => l.id === locationId)?.name;
  const manualEntries = Object.entries(manual);
  const docRequired = feature("require.count_document", true);
  const needDoc = docRequired && !docUrl;

  const diffCls = (d) => (d > 0 ? "text-emerald-600" : d < 0 ? "text-red-600" : "text-slate-400");

  return (
    <div className="space-y-4 fade-in" data-testid="count-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-brand-green" />
          <div>
            <p className="overline">Stok</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Depo Sayımı</h1>
            <p className="text-xs text-slate-500">Sayım föyünü doldurup yükleyin; sistem farkı otomatik hesaplar ve düzeltme önerir.</p>
          </div>
        </div>
        {isPrivileged ? (
          <div className="relative">
            <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-green" />
            <select data-testid="count-location" value={locationId} onChange={(e) => { setLocationId(e.target.value); reset(); setManual({}); }}
              className="h-10 appearance-none rounded-md border border-input bg-white pl-8 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-green">
              {locations.filter((l) => l.is_active !== false).map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type_label})</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        ) : (
          <div data-testid="count-location-fixed" className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
            <MapPin className="h-4 w-4 text-brand-green" /> {user?.location_name || "Lokasyonunuz"}
          </div>
        )}
      </div>

      {/* Adım 1 — Föy hazırlama */}
      <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
        <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">1 · Sayım Föyü Hazırla</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="count-download-template" onClick={downloadTemplate} variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
            <Download className="mr-1.5 h-4 w-4" /> Excel Şablonu (Sistem Stoğu)
          </Button>
          <Button data-testid="count-print-blank" onClick={() => printBlankTemplate("count")} variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50">
            <Printer className="mr-1.5 h-4 w-4" /> Boş A4 Föy (QR'lı) Yazdır
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Excel şablonu tüm ürünleri sistem stoğuyla listeler — sadece <b>Sayılan</b> sütununu doldurup geri yükleyin. A4 föy fiziksel sayım + imza içindir (QR ile izlenir).</p>
      </div>

      {schedule && schedule.items?.length > 0 && (
        <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="count-schedule">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
              <ClipboardCheck className="h-3.5 w-3.5" /> Sayım Takvimi
              {schedule.overdue_count > 0 && <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">{schedule.overdue_count} gecikmiş</span>}
              {schedule.upcoming_count > 0 && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">{schedule.upcoming_count} yaklaşan</span>}
            </p>
            {canAdjust && <Button data-testid="count-schedule-save" onClick={saveSchedule} size="sm" variant="outline" className="border-brand-green text-brand-green hover:bg-emerald-50">Periyotları Kaydet</Button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="px-3 py-2 font-medium">Depo / Mağaza</th>
                <th className="px-3 py-2 font-medium">Son Sayım</th>
                <th className="px-3 py-2 text-right font-medium">Periyot (gün)</th>
                <th className="px-3 py-2 font-medium">Sonraki Sayım</th>
                <th className="px-3 py-2 font-medium">Durum</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {schedule.items.map((it) => (
                  <tr key={it.location_id} data-testid={`count-sched-${it.location_id}`} className={it.overdue ? "bg-red-50/40" : it.upcoming ? "bg-amber-50/40" : ""}>
                    <td className="px-3 py-2 font-medium text-slate-700">{it.location_name}</td>
                    <td className="px-3 py-2 text-slate-500">{it.last_count_at ? it.last_count_at.slice(0, 10) : "hiç"}{it.days_since != null ? ` · ${it.days_since}g önce` : ""}</td>
                    <td className="px-3 py-2 text-right">
                      {canAdjust ? (
                        <input data-testid={`count-freq-${it.location_id}`} value={freqEdits[it.location_id] ?? it.frequency_days} onChange={(e) => setFreqEdits({ ...freqEdits, [it.location_id]: e.target.value.replace(/[^0-9]/g, "") })} className="h-8 w-16 rounded-md border border-slate-200 px-2 text-right text-sm tabular" />
                      ) : <span className="tabular text-slate-600">{it.frequency_days}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{it.next_due_at || "—"}{(it.days_left != null && !it.overdue) ? ` · ${it.days_left}g kaldı` : ""}</td>
                    <td className="px-3 py-2">
                      {it.overdue ? <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700"><AlertTriangle className="h-3 w-3" /> Gecikmiş</span>
                        : it.upcoming ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">Yaklaşıyor</span>
                          : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Güncel</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Adım 2 — Sayım yükle / gir */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">2a · Sayım Belgesi Yükle (İncelemeye Gider)</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="count-import-input" onChange={importSheet} />
          <Button data-testid="count-import-btn" onClick={() => fileRef.current?.click()} disabled={loading}
            className="w-full bg-brand-green text-white hover:bg-brand-green-600">
            {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
            Sayım Belgesi Yükle (Excel / PDF / Foto)
          </Button>
          <p className="mt-2 text-[11px] text-slate-400">Excel föyü otomatik okunur ve farklar hesaplanır. PDF/foto belgeler inceleme için eklenir. <b>Belge yüklenen sayım doğrudan uygulanmaz; amir onayına gönderilir.</b></p>
        </div>

        <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">2b · Elle Say (kısmi sayım)</p>
            <Button data-testid="count-camera-btn" onClick={() => { setScanCount(0); setScanOpen(true); }} size="sm" variant="outline" className="h-7 border-brand-green text-brand-green hover:bg-emerald-50">
              <Camera className="mr-1 h-3.5 w-3.5" /> Kamera ile Say
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="count-manual-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün / SKU / barkod ara..." className="pl-9" />
            {results.length > 0 && (
              <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="count-manual-results">
                {results.map((it) => (
                  <button key={it.product_id} data-testid={`count-manual-opt-${it.product_id}`} onClick={() => addManual(it)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                    <span className="min-w-0 truncate"><b className="text-slate-800">{it.name}</b> <span className="text-[10px] text-slate-400">{it.sku}</span></span>
                    <span className="shrink-0 text-xs text-slate-500">sistem {num(it.stock_on_hand)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {manualEntries.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {manualEntries.map(([pid, v]) => (
                <div key={pid} data-testid={`count-manual-row-${pid}`} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{v.name}</span>
                  <Input data-testid={`count-manual-qty-${pid}`} value={v.counted} onChange={(e) => setManualQty(pid, e.target.value)}
                    placeholder="Sayılan" className="h-8 w-24 text-right tabular" />
                  <button data-testid={`count-manual-remove-${pid}`} onClick={() => removeManual(pid)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button data-testid="count-manual-preview" onClick={previewManual} disabled={loading} size="sm" variant="outline" className="mt-1 w-full border-brand-green text-brand-green hover:bg-emerald-50">
                Farkları Hesapla
              </Button>
            </div>
          )}
        </div>
      </div>

      {unmatched.length > 0 && (
        <div data-testid="count-unmatched" className="rounded-md bg-amber-50 px-4 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> {unmatched.length} satır eşleşmedi (barkod/SKU bulunamadı): {unmatched.slice(0, 8).join(", ")}{unmatched.length > 8 ? "…" : ""}
        </div>
      )}

      {/* Adım 3 — Fark özeti + tablo */}
      {summary && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div data-testid="count-sum-total" className="rounded-lg bg-white p-3 ring-1 ring-inset ring-slate-200">
              <p className="text-xs text-slate-500">Sayılan Kalem</p><p className="font-heading text-xl font-bold text-slate-800">{summary.total}</p>
            </div>
            <div data-testid="count-sum-match" className="rounded-lg bg-emerald-50 p-3 ring-1 ring-inset ring-emerald-200">
              <p className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Eşit</p><p className="font-heading text-xl font-bold text-emerald-700">{summary.match}</p>
            </div>
            <div data-testid="count-sum-over" className="rounded-lg bg-sky-50 p-3 ring-1 ring-inset ring-sky-200">
              <p className="flex items-center gap-1 text-xs text-sky-700"><TrendingUp className="h-3.5 w-3.5" /> Fazla ({summary.over})</p><p className="font-heading text-xl font-bold text-sky-700">+{num(summary.over_qty)}</p>
            </div>
            <div data-testid="count-sum-short" className="rounded-lg bg-red-50 p-3 ring-1 ring-inset ring-red-200">
              <p className="flex items-center gap-1 text-xs text-red-600"><TrendingDown className="h-3.5 w-3.5" /> Eksik ({summary.short})</p><p className="font-heading text-xl font-bold text-red-600">−{num(summary.short_qty)}</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="count-only-diff" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} className="h-3.5 w-3.5 accent-brand-green" />
              Yalnız farklı kalemleri göster ({diffLines})
            </label>
            <span className="text-xs text-slate-400">{locName}</span>
          </div>

          <div className="overflow-hidden rounded-md bg-white ring-1 ring-inset ring-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Ürün</th>
                  <th className="px-4 py-2.5 text-right font-medium">Sistem</th>
                  <th className="px-4 py-2.5 text-right font-medium">Sayılan</th>
                  <th className="px-4 py-2.5 text-right font-medium">Fark</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">{onlyDiff ? "Fark yok — tüm sayımlar sistemle eşit." : "Kalem yok."}</td></tr>
                ) : shown.map((r) => (
                  <tr key={r.product_id} data-testid={`count-row-${r.product_id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5"><p className="font-medium text-slate-800">{r.name}</p><p className="font-mono text-[10px] text-slate-400">{r.sku}</p></td>
                    <td className="px-4 py-2.5 text-right tabular text-slate-600">{num(r.system)} <span className="text-[10px] text-slate-400">{r.base_unit}</span></td>
                    <td className="px-4 py-2.5 text-right tabular font-semibold text-slate-800">{num(r.counted)}</td>
                    <td className={`px-4 py-2.5 text-right tabular font-bold ${diffCls(r.diff)}`} data-testid={`count-diff-${r.product_id}`}>{r.diff > 0 ? "+" : ""}{num(r.diff)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Adım 4 — Uygula */}
          {canCount && (
            <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
              <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">4 · Farkları Uygula (Stok Düzelt)</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-600">Açıklama / Not</label>
                  <Input data-testid="count-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. Aylık depo sayımı" className="mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">İmzalı Föy (opsiyonel · QR ile belge no okunur)</label>
                  <input ref={sheetRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="count-sheet-input" onChange={uploadSheet} />
                  <div className="mt-1 flex items-center gap-2">
                    <Button data-testid="count-sheet-btn" onClick={() => sheetRef.current?.click()} variant="outline" size="sm" className="text-slate-600">
                      <FileText className="mr-1.5 h-4 w-4" /> {docUrl ? "Föy Yüklendi" : "İmzalı Föy Yükle"}
                    </Button>
                    {docNo && <span data-testid="count-docno" className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200"><QrCode className="h-3 w-3" /> {docNo}</span>}
                  </div>
                </div>
              </div>
              <Button data-testid="count-apply-btn" onClick={() => setConfirm(true)} disabled={applying || diffLines === 0}
                className="mt-3 w-full bg-brand-green text-white hover:bg-brand-green-600">
                {applying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                {diffLines} Farkı Uygula ve Stoğu Düzelt
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Geçmiş sayımlar */}
      {sessions.length > 0 && (
        <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="count-sessions">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><History className="h-3.5 w-3.5" /> Son Sayımlar</p>
          <div className="space-y-1.5">
            {sessions.map((s) => (
              <div key={s.id} data-testid={`count-session-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs">
                <span className="font-medium text-slate-700">{s.location_name}{s.doc_no ? ` · ${s.doc_no}` : ""}</span>
                <span className="text-slate-500">{s.adjusted_items} düzeltme · <span className="text-sky-600">+{num(s.over_qty)}</span> / <span className="text-red-600">−{num(s.short_qty)}</span> · {(s.created_at || "").slice(0, 16).replace("T", " ")} · {s.created_by}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-md" data-testid="count-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Sayım Farklarını Uygula</DialogTitle>
            <DialogDescription>
              {locName} lokasyonunda <b>{diffLines}</b> kalemin stoğu sayılan değere çekilecek
              (<span className="text-sky-600">+{num(summary?.over_qty || 0)}</span> / <span className="text-red-600">−{num(summary?.short_qty || 0)}</span>).
              {docUrl
                ? <> Belge ekli olduğu için işlem <b>doğrudan uygulanmaz; amir onayına</b> gönderilir.</>
                : docRequired
                  ? <> <b className="text-red-600">Sayım belgesi (imzalı föy/foto/PDF) zorunludur</b> — belge yüklenmeden sayım uygulanamaz.</>
                  : <> Bu işlem stok hareketlerine <b>Sayım Düzeltmesi</b> olarak yazılır ve geri alınamaz.</>}
            </DialogDescription>
          </DialogHeader>
          {needDoc && (
            <div data-testid="count-doc-required" className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-inset ring-amber-200">
              Devam etmek için önce "İmzalı Föy Yükle" ile belge (föy/foto/PDF) yükleyin — her sayım kanıtlı olmalı.
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirm(false)}><X className="mr-1 h-4 w-4" /> Vazgeç</Button>
            <Button data-testid="count-confirm-apply" size="sm" onClick={apply} disabled={applying || needDoc} className="bg-brand-green text-white hover:bg-brand-green-600">
              {applying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Onayla ve Uygula
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="max-w-md" data-testid="count-scan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><ScanLine className="h-5 w-5 text-brand-green" /> Kamera ile Sayım</DialogTitle>
            <DialogDescription>Ürün barkodunu kameraya okutun — her okutma sayılan miktarı <b>+1</b> artırır. Bittiğinde kapatıp farkları hesaplayın.</DialogDescription>
          </DialogHeader>
          <div id="count-scanner-region" data-testid="count-scanner-region" className="overflow-hidden rounded-lg bg-black" style={{ minHeight: 240 }} />
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600" data-testid="count-scan-count">Bu oturumda okunan: <b>{scanCount}</b></span>
            <Button data-testid="count-scan-done" size="sm" onClick={() => setScanOpen(false)} className="bg-brand-green text-white hover:bg-brand-green-600"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Bitir & Listeye Ekle</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
